import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { FIELD_INFO_FALLBACKS } from './field-info-fallbacks';

export interface FieldInfo {
  id: number;
  gridName: string;
  fieldName: string;
  fieldKeyName: string;
  informationText: string | null;
  showInformation: string;
}

export interface FieldInfoResponse {
  data: FieldInfo[];
  message: string;
  customValues: any;
}

export interface FieldInfoUpdatePayload {
  informationText: string;
  showInformation: string;
}

@Injectable({
  providedIn: 'root'
})
export class FieldInfoGridService {
  private readonly baseUrl = `${environment.main_url}/agilelegal`;

  // Global cache for all field info records
  private static _allFields: FieldInfo[] = [];
  private static _fieldsLoaded = false;

  constructor(private http: HttpClient) { }

  /**
   * Fetch all field info records once and cache globally.
   * Returns a promise that resolves when loaded.
   */
  fetchAllFieldsOnce(): Promise<FieldInfo[]> {
    if (FieldInfoGridService._fieldsLoaded && FieldInfoGridService._allFields.length > 0) {
      return Promise.resolve(FieldInfoGridService._allFields);
    }
    return this.http.post<FieldInfoResponse>(`${this.baseUrl}/grid/fields/information/filter`, {}).toPromise()
      .then(res => {
        FieldInfoGridService._allFields = this.applyFallbacks(res.data || []);
        FieldInfoGridService._fieldsLoaded = true;
        return FieldInfoGridService._allFields;
      });
  }

  /**
   * Get cached field info records (after fetchAllFieldsOnce has been called).
   */
  getAllFields(): FieldInfo[] {
    return FieldInfoGridService._allFields;
  }

  private applyFallbacks(fields: FieldInfo[]): FieldInfo[] {
    return fields.map((field: FieldInfo) => {
      const fallback = FIELD_INFO_FALLBACKS[field.gridName]?.[field.fieldKeyName];
      if (!fallback || !this.isPlaceholder(field)) {
        return field;
      }
      return { ...field, informationText: fallback };
    });
  }

  private isPlaceholder(field: FieldInfo): boolean {
    const text = (field.informationText || '').trim();
    return text.toUpperCase() === (field.gridName + '_' + field.fieldKeyName + '_TEST').toUpperCase();
  }

  /**
   * Refresh the global cache (e.g., after an update).
   */
  refreshAllFields(): Promise<FieldInfo[]> {
    return this.http.post<FieldInfoResponse>(`${this.baseUrl}/grid/fields/information/filter`, {}).toPromise()
      .then(res => {
        FieldInfoGridService._allFields = this.applyFallbacks(res.data || []);
        FieldInfoGridService._fieldsLoaded = true;
        return FieldInfoGridService._allFields;
      });
  }

  getFieldInfoList(page?: number, pageSize?: number, payload?: any): Observable<FieldInfoResponse> {
    if (!page && !pageSize) {
      return  this.http.post<FieldInfoResponse>(`${this.baseUrl}/grid/fields/information/filter`,{});
    }
    return this.http.post<FieldInfoResponse>(`${this.baseUrl}/grid/fields/information/filter?pageNumber=${page}&pageSize=${pageSize}`, payload);
  }

  saveFieldInfo(payload: FieldInfoUpdatePayload, id: number): Observable<{ message: string }> {
    // After save, refresh the global cache
    return this.http.put<{ message: string }>(`${this.baseUrl}/grid/fields/information/${id}`, payload);
  }

  /**
   * Call this after saveFieldInfo to refresh the cache.
   */
  saveAndRefresh(payload: FieldInfoUpdatePayload, id: number): Promise<{ message: string }> {
    return this.saveFieldInfo(payload, id).toPromise().then(res => {
      return this.refreshAllFields().then(() => res);
    });
  }
}
